import React, { useState, useEffect } from 'react';
import PageContainer from '../../layouts/PageContainer';
import PageHeader from '../../components/Nav/PageHeader';
import { useNetwork } from '../../DataStore/NetworkContext';
import { Skeleton, SkeletonCircle, SkeletonPill } from '../../util/skeleton/Skeleton';
import AlertDisconnectDevice from '../../components/Voucher/AlertDisconnectDevice';
import { useNavigate } from 'react-router-dom';

/* ---------- helpers ---------- */
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const fmtShort = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      })
    : '—';

const secsToHms = (s) => {
  const sec = Math.max(0, s | 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const r = sec % 60;
  return [h, m, r].map((v) => String(v).padStart(2, '0')).join(':');
};

const badge = (status) => {
  const s = String(status || '').toLowerCase();
  const tone =
    s === 'active' || s === 'used' || s === 'paid'
      ? 'success'
      : s === 'expired'
      ? 'secondary'
      : s === 'revoked'
      ? 'danger'
      : 'warning';
  return <span className={`badge badge-${tone}`}>{status || '—'}</span>;
};

const normalizeVoucher = (val) => {
  if (!val) return '';
  const up = (val + '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  const l = up.slice(0, 3);
  const r = up.slice(3);
  return r ? `${l}-${r}` : l;
};

const isValidVoucher = (v) => /^[A-Z0-9]{3}-[A-Z0-9]{1,5}$/.test(v);

/* ---------- view-model ---------- */
const toViewModel = (info) => {
  if (!info) return null;
  const devices = Array.isArray(info.device_history) ? info.device_history : [];
  return {
    id: info.id,
    code: info.code,
    status: info.status,
    plan: info.plan_name,
    purchasedAt: info.created_at,
    expiresAt: info.expires_at,
    issuer: info.created_by,
    devicesCount: devices.length,
    totalUpStr: info.data_stats?.total_up ?? '0 B',
    totalDownStr: info.data_stats?.total_down ?? '0 B',
    usage: devices.map((d) => ({
      mac: d.mac_address,
      vendor: d.device_name?.manufacturer ?? 'Unknown',
      nickname: d.device_name?.nickname ?? 'Unknown',
      downStr: d.formatted_down ?? '0 B',
      upStr: d.formatted_up ?? '0 B',
      sessions: d.session_count,
      firstUse: d.usage_dates.first_used_at,
      lastUse: d.usage_dates.last_used_at,
      lastLocation: d.usage_dates.last_location ?? '—',
      seconds: d.duration,
      status: d.connection_status || '—',
      device_token: d.device_token, // main identifier for selection
    })),
  };
};

/* ---------- skeleton blocks ---------- */
const SummarySkeleton = () => (
  <div className="card shadow-sm border-0 mb-3">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-center">
        <div style={{ flex: 1 }}>
          <div className="text-muted small">Voucher</div>
          <Skeleton w="220px" h={20} />
        </div>
        <div className="text-right">
          <div className="small text-muted">Status</div>
          <SkeletonPill w={64} h={22} />
        </div>
      </div>

      <div className="row mt-3">
        {[...Array(4)].map((_, i) => (
          <div className="col-md-3 col-6 mb-3" key={i}>
            <div className="text-muted small">
              <Skeleton w="80px" h={12} />
            </div>
            <Skeleton w="140px" h={16} />
          </div>
        ))}
      </div>

      <div className="row text-center mt-2">
        {[...Array(4)].map((_, i) => (
          <div className="col-6 col-md-3 mb-2" key={i}>
            <div className="text-muted small">
              <Skeleton w="70px" h={12} />
            </div>
            <Skeleton w="60px" h={22} />
          </div>
        ))}
      </div>

      <div className="mt-3 p-3 rounded" style={{ background: 'linear-gradient(135deg, #1f2430, #1a1f2b)' }}>
        <div className="d-flex align-items-center">
          <SkeletonCircle size={20} />
          <Skeleton w="260px" h={12} style={{ marginLeft: 8 }} />
        </div>
      </div>
    </div>
  </div>
);

const TableSkeleton = () => (
  <div className="card shadow-sm border-0">
    <div className="card-header bg-transparent border-0 pb-0 d-flex justify-content-between">
      <Skeleton w="200px" h={16} />
      <SkeletonPill w={36} h={20} />
    </div>
    <div className="card-body pt-2">
      <div className="table-responsive">
        <table className="table table-hover table-sm align-middle mb-0">
          <thead className="thead-light">
            <tr>
              {['Device', 'MAC', 'Vendor', 'Download', 'Upload', 'Sessions', 'First use', 'Last use', 'Last location', 'Online'].map(
                (h) => (
                  <th key={h}>
                    <Skeleton w="70%" h={12} />
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {[...Array(6)].map((_, i) => (
              <tr key={i}>
                <td>
                  <Skeleton w="160px" h={14} />
                  <Skeleton w="120px" h={10} style={{ display: 'block', marginTop: 6 }} />
                </td>
                <td>
                  <Skeleton w="140px" h={14} />
                </td>
                <td>
                  <Skeleton w="100px" h={14} />
                </td>
                <td className="text-right">
                  <Skeleton w="60px" h={14} />
                </td>
                <td className="text-right">
                  <Skeleton w="60px" h={14} />
                </td>
                <td className="text-center">
                  <Skeleton w="40px" h={14} />
                </td>
                <td>
                  <Skeleton w="120px" h={14} />
                </td>
                <td>
                  <Skeleton w="120px" h={14} />
                </td>
                <td>
                  <Skeleton w="80px" h={14} />
                </td>
                <td className="text-right">
                  <SkeletonPill w={74} h={20} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

/* ---------- page ---------- */
const VoucherUsagePage = () => {
  const { getVoucherInfo, voucherInfo, fetchingVoucher, setSelectedVoucher } = useNetwork();
  const voucherCodeFromUrl = new URLSearchParams(window.location.search).get('v') || null;

  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [current, setCurrent] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // NOTE: This now stores device_tokens, not MACs
  const [selectedTokens, setSelectedTokens] = useState([]);
  const isAllSelected = current?.usage?.length > 0 && selectedTokens.length === current.usage.length;

  const navigation = useNavigate();

  useEffect(() => {
    if (voucherInfo) {
      setCurrent(toViewModel(voucherInfo));
      setIsFetching(false);
    }
  }, [voucherInfo]);

  useEffect(() => {
    if (voucherCodeFromUrl) {
      setSelectedVoucher(voucherCodeFromUrl);
    }
  }, [voucherCodeFromUrl]);

  const handleInput = (e) => {
    setQuery(normalizeVoucher(e.target.value));
    if (error) setError('');
  };

  const searchVoucher = async (e) => {
    e?.preventDefault?.();
    const code = normalizeVoucher(query);
    if (!isValidVoucher(code)) {
      setError('Enter a valid voucher code, e.g., 241-QZKLM');
      return;
    }
    setError('');
    setIsFetching(true);
    await getVoucherInfo(code);
  };

  const onRefresh = () => {
    if (current?.code) {
      getVoucherInfo(current.code);
    }
  };

  const handleBlockDevice = () => {
    if (selectedTokens.length === 0) return;
    setShowDisconnectModal(true);
  };

  /* ---------- selection now based on device_token ---------- */
  const toggleOne = (deviceToken) => {
    if (!deviceToken) return;
    setSelectedTokens((prev) =>
      prev.includes(deviceToken) ? prev.filter((t) => t !== deviceToken) : [...prev, deviceToken],
    );
  };

  const toggleAll = () => {
    if (!current?.usage?.length) return;
    setSelectedTokens((prev) =>
      prev.length === current.usage.length ? [] : current.usage.map((u) => u.device_token).filter(Boolean),
    );
  };

  const clearSelection = () => setSelectedTokens([]);

  const copySelected = async () => {
    const text = selectedTokens.join(', ');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // no-op
    }
  };

  const handleMacClick = (mac) => {
    navigation(`/network-device?m=${mac}`);
  };

  const showSkeleton = fetchingVoucher;

  return (
    <>
      <PageContainer>
        <PageHeader
          title="Voucher Usage"
          breadcrumb={
            <>
              <li className="breadcrumb-item">Vouchers</li>
              <li className="breadcrumb-item active" aria-current="page">
                Voucher Usage
              </li>
            </>
          }
          dropdown={{
            label: 'Options',
            icon: 'fas fa-cog',
            buttonClass: 'btn-outline-info',
            items: [
              { label: 'Block Device', icon: 'fas fa-ban', onClick: () => handleBlockDevice('tabular') },
              { label: 'Refresh', icon: 'fas fa-sync', onClick: onRefresh },
            ],
          }}
        />

        {/* Search */}
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body py-3">
            <form onSubmit={searchVoucher} className="d-flex align-items-center">
              <div className="mr-3 text-muted small">Search Voucher</div>
              <div className="input-group" style={{ maxWidth: 420 }}>
                <div className="input-group-prepend">
                  <span className="input-group-text bg-dar border-0 text-muted">
                    <i className="fe fe-search" />
                  </span>
                </div>
                <input
                  className="form-control bg-dark border-0 text-light"
                  placeholder="Enter voucher code (e.g., 241-QZKLM)"
                  value={query}
                  onChange={handleInput}
                  spellCheck={false}
                  disabled={isFetching}
                />
                <div className="input-group-append">
                  <button className="btn btn-primary" type="submit" disabled={isFetching}>
                    {isFetching ? 'Searching…' : 'Find'}
                  </button>
                </div>
              </div>
              {error ? <div className="text-danger small ml-3">{error}</div> : null}
            </form>
          </div>
        </div>

        {/* Summary */}
        {showSkeleton ? (
          <SummarySkeleton />
        ) : (
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-body">
              {current ? (
                <>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small">Voucher</div>
                      <h5 className="mb-0 font-weight-bold">{current.code}</h5>
                    </div>
                    <div className="text-right">
                      <div className="small text-muted">Status</div>
                      {badge(current.status)}
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-3 col-6 mb-3">
                      <div className="text-muted small">Plan</div>
                      <div className="font-weight-600">{current.plan}</div>
                    </div>
                    <div className="col-md-3 col-6 mb-3">
                      <div className="text-muted small">Generated On</div>
                      <div>{fmtDate(voucherInfo?.created_at)}</div>
                    </div>
                    <div className="col-md-3 col-6 mb-3">
                      <div className="text-muted small">Expires</div>
                      <div>{fmtShort(voucherInfo?.expires_at)}</div>
                    </div>
                    <div className="col-md-3 col-6 mb-3">
                      <div className="text-muted small">Issuer</div>
                      <div>
                        {voucherInfo.source} | {voucherInfo.msisdn || 'Unknown'}
                      </div>
                    </div>
                  </div>

                  <div className="row text-center mt-2">
                    <div className="col-6 col-md-3 mb-2">
                      <div className="text-muted small">Devices</div>
                      <div className="h5 mb-0">{current.devicesCount}</div>
                    </div>
                    <div className="col-6 col-md-3 mb-2">
                      <div className="text-muted small">Sessions</div>
                      <div className="h5 mb-0">{voucherInfo?.total_sessions}</div>
                    </div>
                    <div className="col-6 col-md-3 mb-2">
                      <div className="text-muted small">Download</div>
                      <div className="h6 mb-0">{current.totalDownStr}</div>
                    </div>
                    <div className="col-6 col-md-3 mb-2">
                      <div className="text-muted small">Upload</div>
                      <div className="h6 mb-0">{current.totalUpStr}</div>
                    </div>
                  </div>

                  <div className="mt-3 p-3 rounded" style={{ background: 'linear-gradient(135deg, #1f2430, #1a1f2b)' }}>
                    <div className="d-flex align-items-center">
                      <i className="fe fe-info mr-2" />
                      <div className="small text-muted">
                        Total online time across devices:{' '}
                        <strong>{secsToHms(voucherInfo?.total_duration)}</strong>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-muted">Search a voucher above to view its usage.</div>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        {showSkeleton ? (
          <TableSkeleton />
        ) : (
          <div className="card shadow-sm border-0">
            <div className="card-header bg-transparent border-0 pb-0">
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0">Devices that used this voucher</h6>
                <div className="d-flex align-items-center">
                  <span className="badge badge-light mr-2">{selectedTokens.length} selected</span>
                  <span className="badge badge-light">{current?.devicesCount || 0} total</span>
                </div>
              </div>

              {/* Selected field */}
              <div className="mt-2 d-flex align-items-center">
                <input
                  className="form-control form-control-sm bg-dark border-0 text-light"
                  style={{ maxWidth: 520 }}
                  readOnly
                  value={selectedTokens.join(', ')}
                  placeholder="Selected device tokens will appear here…"
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary ml-2"
                  onClick={copySelected}
                  disabled={!selectedTokens.length}
                  title="Copy selected device tokens"
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary ml-2"
                  onClick={clearSelection}
                  disabled={!selectedTokens.length}
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="card-body pt-2">
              {current?.usage?.length ? (
                <div className="table-responsive">
                  <table className="table table-hover table-sm align-middle mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: 28 }}>
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="chk-all"
                              checked={isAllSelected}
                              onChange={toggleAll}
                            />
                            <label className="custom-control-label" htmlFor="chk-all" />
                          </div>
                        </th>
                        <th style={{ minWidth: 160 }}>Device</th>
                        <th>MAC</th>
                        <th>Vendor</th>
                        <th className="text-right">Download</th>
                        <th className="text-right">Upload</th>
                        <th className="text-center">Sessions</th>
                        <th className="text-center">Status</th>
                        <th style={{ minWidth: 160 }}>First use</th>
                        <th style={{ minWidth: 160 }}>Last use</th>
                        <th className="text-right">Online</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.usage.map((u) => {
                        const checked = !!u.device_token && selectedTokens.includes(u.device_token);
                        const key = u.device_token || u.mac;
                        const rowId = `chk-${(u.device_token || u.mac || '').replace(/[^A-Za-z0-9]/g, '')}`;
                        return (
                          <tr key={key} className={checked ? 'table-active' : ''}>
                            <td>
                              <div className="custom-control custom-checkbox">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={rowId}
                                  checked={checked}
                                  onChange={() => toggleOne(u.device_token)}
                                  disabled={!u.device_token}
                                />
                                <label className="custom-control-label" htmlFor={rowId} />
                              </div>
                            </td>
                            <td>
                              <div className="font-weight-600">{u.nickname}</div>
                              <div className="text-muted small">on {current.code}</div>
                            </td>
                            <td>
                              <span className="link" onClick={() => handleMacClick(u.device_token)}>
                                <code>{u.mac}</code>
                              </span>
                            </td>
                            <td>{u.vendor}</td>
                            <td className="text-right">{u.downStr}</td>
                            <td className="text-right">{u.upStr}</td>
                            <td className="text-center">{u.sessions}</td>
                            <td className="text-center">{u.status}</td>
                            <td>{fmtDate(u.firstUse)}</td>
                            <td>{fmtDate(u.lastUse)}</td>
                            <td className="text-right">
                              <span className="badge badge-light">{secsToHms(u.seconds)}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted small">No devices have used this voucher yet.</div>
              )}
            </div>

            <style>{`
              .table-active { background-color: rgba(255,255,255,0.03); }
            `}</style>
          </div>
        )}

        <style>{`
          .font-weight-600 { font-weight: 600; }
          .table td, .table th { vertical-align: middle; }
        `}</style>
      </PageContainer>

      <AlertDisconnectDevice
        show={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        // NOTE: this prop now carries device_tokens; update backend accordingly
        selectedMacs={selectedTokens}
        voucherId={current?.id}
        onSuccess={() => {
          setShowDisconnectModal(false);
          clearSelection();
          onRefresh();
        }}
      />
    </>
  );
};

export default VoucherUsagePage;
